import { all, call, fork, put, takeLatest } from 'redux-saga/effects';
import {
  FETCH_AI_SUMMARY,
  FETCH_AI_SUMMARY_LOADING,
  FETCH_AI_SUMMARY_SUCCESS,
  FETCH_AI_SUMMARY_ERROR,
  FETCH_AI_SUMMARY_DONE,
  FETCH_AI_MODELS,
  FETCH_AI_MODELS_LOADING,
  FETCH_AI_MODELS_SUCCESS,
  FETCH_AI_MODELS_ERROR,
  FETCH_AI_MODELS_DONE
} from '../actions/actionTypes';
import AIApiV1 from '../api/AIApiV1';

/**
 * Simplified saga for fetching AI insights using the simplified AIApiV1
 * This follows the same pattern as responsesSaga.js but with a focus on AI insights
 */

export function* watchFetchAIInsightsSaga() {
  yield takeLatest(FETCH_AI_SUMMARY, fetchAIInsightsSaga);
}

export function* fetchAIInsightsSaga(action) {
  const { eventId, modelId, forceRefresh, isSummaryView, questionNumber } = action.payload || {};
  try {
    yield put({ type: FETCH_AI_SUMMARY_LOADING });
    
    // Log the request parameters for debugging
    console.log('Fetching AI insights with:', { 
      eventId, 
      modelId, 
      forceRefresh, 
      isSummaryView, 
      questionNumber 
    });
    
    // First get the event details
    const eventDetails = yield call(AIApiV1.getEventDetails, eventId);
    if (eventDetails.eventDetailsError) {
      throw new Error(`Failed to fetch event details: ${eventDetails.eventDetailsError.message || 'Unknown error'}`);
    }
    
    // Then get the event posts
    const eventPosts = yield call(AIApiV1.getEventPosts, eventId);
    if (eventPosts.postsError) {
      throw new Error(`Failed to fetch event posts: ${eventPosts.postsError.message || 'Unknown error'}`);
    }
    
    // Get event stats
    const eventStats = yield call(AIApiV1.getEventStats, eventId);
    if (eventStats.statsError) {
      throw new Error(`Failed to fetch event stats: ${eventStats.statsError.message || 'Unknown error'}`);
    }
    
    // Combine all the data into a simple insights object
    // This is a simplified version - in a real implementation, you would process this data
    // to generate actual AI insights
    const insights = {
      eventDetails: eventDetails,
      summary: {
        title: eventDetails.title || 'Event Summary',
        totalResponses: eventPosts.posts?.length || 0,
        averageRating: eventStats.average_rating || 'N/A',
        lastUpdated: AIApiV1.formatResponseDate(Date.now() / 1000)
      },
      topThemes: [
        { name: 'Theme 1', count: 5 },
        { name: 'Theme 2', count: 3 },
        { name: 'Theme 3', count: 2 }
      ],
      // Add more placeholder data as needed
    };
    
    yield put({
      type: FETCH_AI_SUMMARY_SUCCESS,
      summary: insights
    });
  } catch (error) {
    console.error('Error in fetchAIInsightsSaga:', error);
    yield put({
      type: FETCH_AI_SUMMARY_ERROR,
      error: error.message || 'Failed to fetch AI insights'
    });
  } finally {
    yield put({ type: FETCH_AI_SUMMARY_DONE });
  }
}

export function* watchFetchAIModelsSaga() {
  yield takeLatest(FETCH_AI_MODELS, fetchAIModelsSaga);
}

export function* fetchAIModelsSaga() {
  try {
    yield put({ type: FETCH_AI_MODELS_LOADING });
    
    // For simplicity, we're just returning a static list of models
    // In a real implementation, you would fetch this from an API
    const models = [
      { id: 'default', name: 'Default AI Model' },
      { id: 'gpt-4', name: 'GPT-4' },
      { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo' }
    ];
    
    yield put({
      type: FETCH_AI_MODELS_SUCCESS,
      models
    });
  } catch (error) {
    console.error('Error in fetchAIModelsSaga:', error);
    yield put({
      type: FETCH_AI_MODELS_ERROR,
      error: error.message || 'Failed to fetch AI models'
    });
  } finally {
    yield put({ type: FETCH_AI_MODELS_DONE });
  }
}

export default [
  fork(watchFetchAIInsightsSaga),
  fork(watchFetchAIModelsSaga)
];
